import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import BattleField from './battle/BattleField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINGDOMS = [
  {
    name: 'Italy',
    title: 'Regno d\'Italia',
    motto: 'Gloria e Conquista',
    colors: { primary: '#009246', secondary: '#CE2B37', accent: '#fbbf24' },
    gradient: 'linear-gradient(135deg, #0a1f0a 0%, #1a3d1a 40%, #1f0a0a 100%)',
    border: 'rgba(0,146,70,0.7)',
    glow: 'rgba(0,146,70,0.3)',
    shield: { left: '#009246', right: '#CE2B37', center: '#fff' },
    heraldry: '⚜',
    description: 'Astuzia e diplomazia. Il regno che vince con la mente.',
  },
  {
    name: 'Spain',
    title: 'Torneo di Spagna',
    motto: 'Plus Ultra',
    colors: { primary: '#c60b1e', secondary: '#ffc400', accent: '#c60b1e' },
    gradient: 'linear-gradient(135deg, #1f0505 0%, #3d1010 40%, #2a1800 100%)',
    border: 'rgba(198,11,30,0.7)',
    glow: 'rgba(255,196,0,0.3)',
    shield: { left: '#c60b1e', right: '#ffc400', center: '#c60b1e' },
    heraldry: '🦁',
    description: 'Forza e onore. Il regno che conquista con il coraggio.',
  },
  {
    name: 'France',
    title: 'Torneo di Francia',
    motto: 'Liberté et Gloire',
    colors: { primary: '#002395', secondary: '#ED2939', accent: '#fff' },
    gradient: 'linear-gradient(135deg, #050520 0%, #0a0a40 40%, #1f0505 100%)',
    border: 'rgba(0,35,149,0.7)',
    glow: 'rgba(0,35,149,0.35)',
    shield: { left: '#002395', right: '#ED2939', center: '#fff' },
    heraldry: '⚜',
    description: 'Eleganza e strategia. Il regno della raffinatezza.',
  },
  {
    name: 'England',
    title: 'Torneo d’Inghilterra',
    motto: 'Dieu et Mon Droit',
    colors: { primary: '#C8102E', secondary: '#fff', accent: '#C8102E' },
    gradient: 'linear-gradient(135deg, #1f0505 0%, #3d0a0a 40%, #1a1a1a 100%)',
    border: 'rgba(200,16,46,0.7)',
    glow: 'rgba(200,16,46,0.3)',
    shield: { left: '#C8102E', right: '#fff', center: '#C8102E' },
    heraldry: '🦁',
    description: 'Tradizione e potere. Il regno che non cede mai.',
  },
];

// Coat of arms SVG for each kingdom
const CoatOfArms = ({ kingdom, size = 80 }) => {
  const s = kingdom.shield;
  return (
    <svg width={size} height={size * 1.15} viewBox="0 0 100 115">
      {/* Shield shape */}
      <defs>
        <clipPath id={`shield-clip-${kingdom.name}`}>
          <path d="M5 5 L95 5 L95 65 Q95 95 50 110 Q5 95 5 65 Z" />
        </clipPath>
        <linearGradient id={`sg-${kingdom.name}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={s.left} />
          <stop offset="50%" stopColor={s.center} stopOpacity="0.15" />
          <stop offset="100%" stopColor={s.right} />
        </linearGradient>
      </defs>
      {/* Shield body */}
      <path d="M5 5 L95 5 L95 65 Q95 95 50 110 Q5 95 5 65 Z"
        fill={`url(#sg-${kingdom.name})`}
        stroke={kingdom.border?.replace('0.7', '1') || '#fbbf24'}
        strokeWidth="3"
      />
      {/* Vertical divider */}
      <line x1="50" y1="5" x2="50" y2="108" stroke="rgba(255,215,0,0.4)" strokeWidth="1" />
      {/* Horizontal divider */}
      <line x1="5" y1="57" x2="95" y2="57" stroke="rgba(255,215,0,0.4)" strokeWidth="1" />
      {/* Gold rim */}
      <path d="M5 5 L95 5 L95 65 Q95 95 50 110 Q5 95 5 65 Z"
        fill="none"
        stroke="rgba(255,215,0,0.6)"
        strokeWidth="2"
      />
      {/* Center emblem */}
      <circle cx="50" cy="55" r="16" fill="rgba(0,0,0,0.4)" stroke="rgba(255,215,0,0.5)" strokeWidth="1.5" />
      <text x="50" y="61" textAnchor="middle" fontSize="16" fill="#ffd700">♛</text>
    </svg>
  );
};

const KingdomCard = ({ kingdom, isSelected, onClick }) => (
  <motion.button
    data-testid={`kingdom-${kingdom.name.toLowerCase()}`}
    onClick={onClick}
    whileHover={{ scale: 1.03, y: -3 }}
    whileTap={{ scale: 0.97 }}
    style={{
      position: 'relative',
      borderRadius: 16,
      border: `2px solid ${isSelected ? '#fbbf24' : kingdom.border}`,
      background: kingdom.gradient,
      overflow: 'hidden',
      padding: '20px 16px 16px',
      cursor: 'pointer',
      textAlign: 'left',
      boxShadow: isSelected
        ? `0 0 30px ${kingdom.glow}, 0 0 60px ${kingdom.glow}`
        : `0 4px 20px rgba(0,0,0,0.5)`,
      transition: 'box-shadow 0.3s',
    }}
  >
    {/* Shine overlay */}
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      background: `radial-gradient(ellipse at 20% 0%, ${kingdom.glow} 0%, transparent 65%)`,
    }} />

    {/* Selected ring */}
    {isSelected && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          position: 'absolute', inset: -1,
          borderRadius: 16,
          border: '2px solid #fbbf24',
          boxShadow: `0 0 20px rgba(251,191,36,0.5)`,
          pointerEvents: 'none',
        }}
      />
    )}

    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', position: 'relative' }}>
      {/* Coat of arms */}
      <div style={{ flexShrink: 0 }}>
        <CoatOfArms kingdom={kingdom} size={64} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.15em',
          color: 'rgba(251,191,36,0.7)', textTransform: 'uppercase', marginBottom: 3,
        }}>
          {kingdom.title}
        </div>
        <div style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 20, fontWeight: 700, marginBottom: 4,
          background: `linear-gradient(to bottom, #fff, ${kingdom.colors.primary})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {kingdom.name}
        </div>
        <div style={{
          fontSize: 11, fontStyle: 'italic', color: 'rgba(255,255,255,0.5)',
          marginBottom: 8, letterSpacing: '0.05em',
        }}>
          "{kingdom.motto}"
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
          {kingdom.description}
        </div>
      </div>
    </div>

    {/* Selected checkmark */}
    <AnimatePresence>
      {isSelected && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 24, height: 24, borderRadius: '50%',
            background: '#fbbf24',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, color: '#1c1917', fontWeight: 900,
          }}
        >
          ✓
        </motion.div>
      )}
    </AnimatePresence>
  </motion.button>
);

const KingdomSelection = ({ user }) => {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSelect = async () => {
    if (!selected) { toast.error('Seleziona un regno'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/game/select-kingdom`, { user_id: user.user_id, kingdom: selected.name });
      toast.success(`Benvenuto nel ${selected.title}!`);
      navigate('/lobby');
    } catch (error) {
      toast.error('Errore nella selezione del regno');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: '#03050a',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '20px 16px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* The chosen kingdom's battlefield, previewed live behind the crests */}
      <BattleField kingdom={selected?.name || 'Unknown'} dimmed />

      {selected && (
        <motion.div
          key={selected.name}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
            background: `radial-gradient(ellipse at 50% 100%, ${selected.glow} 0%, transparent 70%)`,
          }}
        />
      )}

      {/* Back button */}
      <button onClick={() => navigate('/games')} style={{
        position: 'fixed', top: 14, left: 16, zIndex: 50,
        background: 'rgba(15,15,20,0.85)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10,
        padding: '6px 14px', color: '#fbbf24', fontSize: 13, fontWeight: 700,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ← Survival Games
      </button>

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 720 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: 'center', marginBottom: 32 }}
        >
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '0.2em',
            color: 'rgba(251,191,36,0.6)', textTransform: 'uppercase', marginBottom: 8,
          }}>
            The Last Kingdom
          </div>
          <h1 style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 'clamp(22px, 5vw, 38px)', fontWeight: 900, margin: '0 0 8px',
            background: 'linear-gradient(to bottom, #fff 0%, #FFEC80 40%, #FFD700 70%, #B8860B 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.3))',
          }}>
            Scegli il Campo di Battaglia
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0 }}>
            Benvenuto, <span style={{ color: '#fbbf24', fontWeight: 700 }}>{user.username}</span>. In quale regno vuoi conquistare la corona?
          </p>
        </motion.div>

        {/* Kingdom grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14, marginBottom: 24,
        }}>
          {KINGDOMS.map((k, i) => (
            <motion.div
              key={k.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <KingdomCard kingdom={k} isSelected={selected?.name === k.name} onClick={() => setSelected(k)} />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <AnimatePresence>
          {selected && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            >
              <motion.button
                data-testid="continue-button"
                onClick={handleSelect}
                disabled={loading}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                animate={{ boxShadow: ['0 0 10px rgba(251,191,36,0.3)', '0 0 24px rgba(251,191,36,0.7)', '0 0 10px rgba(251,191,36,0.3)'] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                style={{
                  width: '100%', padding: '16px',
                  background: 'linear-gradient(145deg, #fbbf24, #d97706)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: 14, fontSize: 16, fontWeight: 900,
                  color: '#1c1917', letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  fontFamily: "'Cinzel', serif",
                }}
              >
                {loading ? 'Accesso in corso...' : `Combatti per ${selected.name} →`}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default KingdomSelection;
